<script lang="ts" setup>
import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { useSessionStorage } from '@vueuse/core'
import { loginConfig as rocnovelConfig } from './configs/rocnovel'
import { loginConfig as ocereadConfig } from './configs/oceread'
import { loginConfig as talewaveConfig } from './configs/talewave'
import { loginConfig as ficmoonConfig } from './configs/ficmoon'
import { loginConfig as novelarcConfig } from './configs/novelarc'
import { loginConfig as storyellaConfig } from './configs/storyella'
import { loginConfig as silktaleConfig } from './configs/silktale'
import { loginConfig as pearlaConfig } from './configs/pearla'
import { loginConfig as fictoraConfig } from './configs/fictora'
import { loginConfig as marinovelConfig } from './configs/marinovel'
import { useHistoryStore } from '@/stores'
import { useTripartiteLogin } from '@/composables/useTripartiteLogin'
import { useEmailLogin } from '@/composables/useEmailLogin'
import { directPageViewTrack, useElementClick } from '@/composables/tracks'
import { loadBrandConfig } from '@/utils/loadBrandConfig'
import { LOCALE_MAP, type LocaleCode, setLocale } from '@/i18n'

const config = loadBrandConfig({
  rocnovel: rocnovelConfig,
  oceread: ocereadConfig,
  talewave: talewaveConfig,
  ficmoon: ficmoonConfig,
  novelarc: novelarcConfig,
  storyella: storyellaConfig,
  silktale: silktaleConfig,
  pearla: pearlaConfig,
  fictora: fictoraConfig,
  marinovel: marinovelConfig,
})

const route = useRoute()
const router = useRouter()
const historyStore = useHistoryStore()
const showLogout = localStorage.getItem('logout')
const deviceUuid = useSessionStorage<any>('did', undefined)
const redirect = useSessionStorage<any>('redirect', undefined)
const loginToken = useSessionStorage<any>('loginToken', undefined)
const { elementClickForLogin } = useElementClick()

const redirectUrl = route.query.redirect as string
const d = route.query.d as string
const t = route.query.t as string
const from = (route.query.from || 'Profile') as string
const lang = route.query.lang as string | undefined

const init = function () {
  // If host passes lang, honor it first (and persist)
  if (lang && (lang in LOCALE_MAP))
    setLocale(lang as LocaleCode)

  if (redirectUrl)
    redirect.value = redirectUrl

  const query = { ...route.query }
  if (d) {
    deviceUuid.value = d
    delete query.d
  }

  if (t) {
    loginToken.value = t
    delete query.t
  }

  // Clean up transient query params
  if (lang)
    delete query.lang

  router.replace({
    query: { ...query },
  })

  directPageViewTrack('Login', from)
}
init()

const availableLogins = computed(() => {
  try {
    const appConfig = localStorage.getItem('AppConfig')
    if (!appConfig)
      return []
    const configData = JSON.parse(appConfig)
    return configData.login || []
  }
  catch {
    return []
  }
})

if (availableLogins.value.length === 0 && !route.query.emailLogin) {
  router.replace({
    query: {
      ...route.query,
      emailLogin: '1',
    },
  })
}

const { skeleton, signInWithGoogle, signInWithFacebook, signInWithApple, logout } = useTripartiteLogin(deviceUuid.value, loginToken.value, redirect.value)

const isEmailLogin = computed({
  get: () => ~~route.query.emailLogin,
  set: (v: any) => (route.query.emailLogin = `${v}`),
})

function setEmailLogin() {
  elementClickForLogin('login_email', {
    route_title: from,
    route_host: new URL(redirect.value).host,
    route_url: redirect.value,
  })
  historyStore.$push(window.location.href)
  router.push({
    query: {
      ...route.query,
      emailLogin: '1',
    },
  })
}

const back = function () {
  window.history.go(-1)
}

const { form, getCode, beforeSignIn, resetEmail } = useEmailLogin(deviceUuid.value, loginToken.value, redirect.value)

watch(
  () => isEmailLogin.value,
  () => resetEmail(),
)

// 从环境变量获取链接
const userAgreementUrl = import.meta.env.VITE_USER_AGREEMENT_URL
const privacyPolicyUrl = import.meta.env.VITE_PRIVACY_POLICY_URL
const facebookBlue = config.theme.facebookBlue
const googleBlue = config.theme.googleBlue
const appleDark = config.theme.appleDark
const loginBgUrl = `url(${config.resources.loginBg})`
</script>

<template>
  <div class="login-page">
    <div v-if="!skeleton" class="login-bg">
      <div class="fixed top-32px left-12px" @click="back()">
        <i-icon-park-outline-left class="w-6 h-6 color-n-text-2" />
      </div>

      <div v-if="!isEmailLogin" class="mt-180px flex flex-col items-center font-medium">
        <img :src="config.resources.logo" width="80">
        <img :src="config.resources.novel" width="108" class="mt-4">
        <div class="mt-12" />
        <div
          v-if="availableLogins.includes('facebook')"
          class="mt-4 relative w-300px h-45px border-rd-25px text-n-text-inverse flex items-center justify-center facebook-btn login-third-btn"
          @click="signInWithFacebook"
        >
          <img :src="config.resources.facebookIcon" width="20" class="absolute top-50% mt--10px left-30px">
          <span class="login-third-btn__text">{{ config.texts.signInWithFacebook }}</span>
        </div>
        <div
          v-if="availableLogins.includes('google')"
          class="mt-4 relative w-300px h-45px border-rd-25px text-n-text-inverse flex items-center justify-center google-btn login-third-btn"
          @click="signInWithGoogle"
        >
          <img :src="config.resources.googleIcon" width="20" class="absolute top-50% mt--10px left-30px">
          <span class="login-third-btn__text">{{ config.texts.signInWithGoogle }}</span>
        </div>
        <div
          v-if="availableLogins.includes('apple')"
          class="mt-4 relative w-300px h-45px border-rd-25px text-n-text-inverse flex items-center justify-center apple-btn login-third-btn"
          @click="signInWithApple"
        >
          <img :src="config.resources.appleIcon" width="20" class="absolute top-50% mt--10px left-30px">
          <span class="login-third-btn__text">{{ config.texts.signInWithApple }}</span>
        </div>

        <div v-if="showLogout === '1'" class="mt-4 relative w-300px h-45px border-rd-25px text-n-text-inverse flex items-center justify-center google-btn" @click="logout">
          {{ config.texts.logout }}
        </div>
        <div class="mt-30px text-n-primary text-center" @click="setEmailLogin">
          {{ config.texts.continueWithEmail }}
        </div>
      </div>

      <div v-else class="mt-180px flex flex-col items-center">
        <div class="text-20px font-bold text-n-text-1">
          {{ config.texts.signInWithEmail }}
        </div>

        <div class="w-300px">
          <div class="text-n-text-3 text-12px mt-10px mb-5px">
            {{ config.texts.yourEmail }}
          </div>
          <div
            class="flex items-center space-x-5 relative border border-1px rounded-full overflow-hidden"
            :style="{ borderColor: config.theme.inputBorder }"
          >
            <img class="w-4 h-4 absolute left-4 top-4" :src="config.resources.emailIcon">
            <input
              v-model="form.email"
              class="!ml0 flex-1 h-11 w-full pl-10 pr-4 rounded-full border-0 !outline-0 !bg-transparent text-n-text-1"
              placeholder=""
              maxlength="50"
            >
          </div>
        </div>

        <div class="w-300px">
          <div class="text-n-text-3 text-12px mt-10px mb-5px">
            {{ config.texts.verificationCode }}
          </div>
          <div
            class="flex items-center space-x-5 relative border border-1px rounded-full overflow-hidden"
            :style="{ borderColor: config.theme.inputBorder }"
          >
            <img class="w-4 h-4 absolute left-4 top-4" :src="config.resources.emailCodeIcon">
            <input
              v-model="form.code"
              class="!ml0 flex-1 h-11 w-full pl-10 pr-4 rounded-full border-0 !outline-0 !bg-transparent text-n-text-1"
              placeholder=""
              maxlength="6"
            >
            <div v-if="form.timeout > 0" class="text-n-text-3 text-align-left pr-3">
              {{ form.timeout }}s
            </div>
            <div
              v-else
              class=" text-n-primary text-12px text-align-left pr-3"
              @click="getCode"
            >
              {{ config.texts.getVerificationCode }}
            </div>
          </div>
        </div>

        <div class="w-300px h-45px text-n-text-inverse btn-solid line-height-45px text-center border-rd-23px mt-30px" @click="beforeSignIn">
          {{ config.texts.signIn }}
        </div>
      </div>

      <div class="fixed bottom-48px w-282px text-n-text-3 text-12px text-center">
        {{ config.texts.userAgreement }} <a :href="userAgreementUrl">{{ config.texts.userAgreementLink }}</a> {{ config.texts.and }} <a :href="privacyPolicyUrl">{{ config.texts.privacyPolicyLink }}</a>
      </div>
    </div>

    <div v-else class="h-100dvh">
      <div class="pt-20vh flex justify-center">
        <BasicSpinner class="mx-auto" />
      </div>
      <p class="mt-4 text-center font-bold">
        {{ config.texts.loading }}
      </p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.login-bg {
  width: 100vw;
  height: 100vh;
  background-image: v-bind(loginBgUrl);
  background-size: 100% 100%;
  display: flex;
  justify-content: center;
  position: relative;

  a {
    text-decoration: underline;
  }
}

/*
 * Third-party login buttons:
 * - Keep icon at fixed left position
 * - Center text visually
 * - If i18n text is long, ellipsis instead of overflow
 */
.login-third-btn {
  padding-left: 56px; /* reserve space for left icon (left:30px + icon:20px + gap) */
  padding-right: 16px;
  box-sizing: border-box;
}

.login-third-btn__text {
  display: block;
  width: 100%;
  min-width: 0;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.facebook-btn {
  background-color: v-bind(facebookBlue);
}

.google-btn {
  background-color: v-bind(googleBlue);
}

.apple-btn {
  background-color: v-bind(appleDark);
}
</style>
